<template>
  <div
    class="select base-input"
    :style="{ width }"
    @focusout="blurHandler"
    ref="select"
    tabindex="0"
  >
    <label
      v-if="label"
      class="input__label"
      :class="[{ disabled }]"
      @click="toggleDialog"
    >
      {{ label }}{{ required ? '*' : '' }}
    </label>

    <!-- readonly -->
    <div
      v-if="readonly"
      style="display: flex; font-size: 16px; margin-top: 4px"
    >
      <span v-if="shownSelected === placeholder"> - </span>

      <slot
        v-else-if="!multiple"
        name="item"
        :item="shownSelected"
      >
        {{ shownSelected }}
      </slot>

      <span v-else>
        <slot
          v-for="item in inputValue"
          name="item"
          :item="item"
        >
          {{ item }}
        </slot>
      </span>
    </div>

    <!-- selectable -->
    <div class="selectable-content" v-else>
      <!-- field -->
      <div
        :class="['select-field', { disabled, error: hasValidationError }]"
        :style="styleSelected"
        @click.stop="toggleDialog"
      >
        <div v-if="shownSelected" style="width: 90%">
          <div
            v-if="search && (selectDialog || shownSelected === placeholder) && !disabled"
            class="d-flex ai-c"
          >
            <!-- search -->
            <svg
              v-if="!multiple && shownSelected !== placeholder && !disabled"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style="margin-right: 9px; min-width: 20px"
            >
              <path fill-rule="evenodd" clip-rule="evenodd" d="M9.61761 11.0317C8.73155 11.6424 7.65754 12 6.5 12C3.46243 12 1 9.53757 1 6.5C1 3.46243 3.46243 1 6.5 1C9.53757 1 12 3.46243 12 6.5C12 7.65746 11.6425 8.73141 11.0318 9.61744L15.0011 13.5868L13.5869 15.001L9.61761 11.0317ZM10 6.5C10 8.433 8.433 10 6.5 10C4.567 10 3 8.433 3 6.5C3 4.567 4.567 3 6.5 3C8.433 3 10 4.567 10 6.5Z" fill="#b8cad5" fill-opacity="0.35"/>
            </svg>
            <input
              :placeholder="placeholder"
              v-model="searchValue"
              ref="input"
              @keypress.enter.prevent="searchItems"
              :style="(!multiple && shownSelected !== placeholder && !disabled) ? 'margin-left: 3px;' : ''"
            />
          </div>

          <slot
            v-else-if="shownSelected !== placeholder"
            name="item"
            :item="shownSelected"
          >
            {{ shownSelected }}
          </slot>

          <span v-else>{{ placeholder }}</span>
        </div>

        <div v-if="multiple && !onlyPlaceholder" class="multiple">
          <div
            v-for="(item, index) in inputValue"
            :key="index"
            class="selected__item"
            :class="{ item__background: selectedWithBackground }"
          >
            <span style="width: fit-content">
              <slot name="item" :item="itemText ? item[itemText] : item">
                {{ itemText ? item[itemText] : item }}
              </slot>
            </span>

            <button v-if="!disabled" class="multi__clear_icon" type="button" @click="deleteItem(item)">
              <span class="close" />
            </button>
          </div>
        </div>

        <span class="icons__control">
          <!-- clear -->
            <span
              v-if="visibleBtnReset"
              class="close"
              @click.stop="onClear"
            />

          <!-- arrow -->
            <svg
              v-if="!disabled"
              width="15"
              height="10"
              viewBox="0 0 15 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              :class="['dropdown_icon', { active: selectDialog }]"
              style="margin-left: 10px"
            >
              <path d="M1 1L7 7L13 1" stroke="#0E619F" stroke-width="2"/>
            </svg>
        </span>
      </div>

      <!-- error message -->
      <slot v-if="hasValidationError" />

      <!-- dialog-window -->
      <transition :duration="200" name="select">
        <div
          v-show="selectDialog"
          ref="selectDialog"
          :class="['selectDialog', { before: !insideWindow }]"
        >
          <span
            v-for="(item, index) in shownItems"
            :key="index"
            @click.stop="changeSelected(item)"
            :class="['item', { selected: isSelected(item) }]"
            :style="{ height: `${height}px` }"
          >
            <Checkbox v-if="withCheckbox" :value="isSelected(item)" />

            <slot name="item" :item="item">
              <span style="width: fit-content">
                {{ itemText ? item[itemText] : item }}
              </span>
            </slot>
          </span>

          <div
            v-if="!shownItems || !shownItems.length"
            class="item"
            style="pointer-events: none; margin: 0 auto"
          >
            нет вариантов
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import Base from './Vinput/Base/Base.vue'
import Checkbox from './Checkbox.vue'
import { mixinSelect } from '@/mixins/form/mixinSelect'
import { SUFFIX_SEARCH } from '@/const/app'

export default {
  name: 'VSelect',
  components: { Checkbox },
  extends: Base,
  mixins: [mixinSelect],
  props: {
    value: {
      type: [Object, Array],
      default: null
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '100%'
    },
    itemText: {
      type: String,
      default: 'name'
    },
    search: {
      type: [Boolean, String],
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    selectedWithBackground: {
      type: Boolean,
      default: false
    },
    resetIfSame: {
      type: Boolean,
      default: false
    },
    sort: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    withCheckbox: {
      type: Boolean,
      default: false
    },
    onlyPlaceholder: {
      type: Boolean,
      default: false
    },
    userSelect: {
      type: Boolean,
      default: false
    }
  },

  model: {
    prop: 'value',
    event: 'change'
  },

  data () {
    return {
      defaultValue: this.multiple ? [] : this.value
    }
  },

  computed: {
    inputValue: {
      get () {
        if (this.formData && this.name) {
          if (this.userSelect && this.multiple && this.formData[this.name]) {
            const data = this.formData[this.name].map(item => {
              if (item.profile) {
                const newItem = {
                  id: item.id,
                  name: item.profile.full_name
                }
                return newItem
              } else return item
            })
            return data || this.defaultValue
          } else if (this.userSelect && this.formData[this.name]?.profile) {
            const data = {
              id: this.formData[this.name].id,
              name: this.formData[this.name].profile.full_name
            }
            return data || this.defaultValue
          } else return this.formData[this.name] || this.defaultValue
        } else return this.value || this.defaultValue
      },
      set (val) {
        if (this.formData && this.name) {
          Vue.set(this.formData, this.name, val)
        }
        this.$emit('change', val)
      }
    },
    shownSelected () {
      if (this.multiple) {
        return this.inputValue?.length
          ? this.onlyPlaceholder
            ? `выбрано: ${this.inputValue.length}`
            : ''
          : this.placeholder
      }

      if (!this.inputValue || !this.hasSelected) {
        return this.placeholder
      }

      if (this.itemText) {
        return this.inputValue[this.itemText]
      }

      return this.inputValue
    },

    hasSelected () {
      if (this.inputValue instanceof Object) {
        return Object.keys(this.inputValue).length
      } else if (this.inputValue instanceof Array) {
        return this.inputValue.length
      } else {
        return !!this.inputValue
      }
    },
    styleSelected () {
      return {
        color: !this.hasSelected ? '#B8CAD5' : null,
        width: this.width,
        height: this.height
      }
    },
    rules () {
      const validators = {}

      if (this.required) {
        if (!this.inputValue) {
          if (this.isClearSearchValueOnBlur) {
            validators.required = () => false
          } else {
            validators.required = () => this.searchValue
          }
        }

        if (this.inputValue && this.inputValue instanceof Object) {
          validators.required = Object.keys(this.inputValue).length
        }
        if (this.inputValue && this.inputValue instanceof Array) {
          validators.required = () => !!this.inputValue?.length
        }
      }

      if (this.maxLength) {
        validators.maxLength = this.validators.maxLength(+this.maxLength)
      }

      if (this.minLength && !this.isClearSearchValueOnBlur) {
        const length = +this.minLength

        validators.minLength = () => {
          if (this.inputValue) return true

          return this.searchValue.length >= length
        }
      }

      if (this.customValidation) {
        validators.customValidation = value => this.customValidation(value)
      }

      return validators
    },
    visibleBtnReset () {
      if (this.disabled) return false
      return this.multiple ? this.inputValue?.length : this.inputValue
    }
  },

  watch: {
    searchValue (value) {
      if (this.isClearSearchValueOnBlur) return

      this.setFormDataSearchValue(value)
    }
  },

  methods: {
    toggleDialog () {
      if (this.loading || this.readonly || this.disabled) return

      if (this.search) {
        if (!this.selectDialog) {
          this.selectDialog = true
        }

        this.$nextTick(() => this.$refs.input?.focus())
      } else {
        this.selectDialog = !this.selectDialog
      }
    },
    isSelected (item) {
      if (!this.inputValue) {
        return false
      }

      if (Array.isArray(this.inputValue)) {
        return this.inputValue.some((el) => el === item)
      } else {
        return this.inputValue === item
      }
    },
    changeSelected (item) {
      if (this.multiple) {
        let copy = this.inputValue

        if (copy.find((el) => el === item)) {
          copy = copy.filter((el) => el !== item)
        } else {
          copy = [...copy, item]
        }
        if (this.inputValue !== copy) {
          this.inputValue = copy
        }
      } else {
        if (this.inputValue !== item) {
          this.inputValue = item
        } else if (this.resetIfSame) {
          this.inputValue = {}
        }
      }
      if (!this.withCheckbox) this.selectDialog = false
    },
    deleteItem (item) {
      this.inputValue = this.inputValue.filter((el) => el !== item)
    },
    blurHandler (event) {
      const enteringParent = this.$refs.select.contains(event.relatedTarget)

      if (!enteringParent) {
        this.selectDialog = false

        if (this.search && this.isClearSearchValueOnBlur) this.searchValue = ''
      }
    },
    onClear () {
      if (this.multiple) {
        this.inputValue = []
        this.defaultValue = null
      } else {
        this.inputValue = null
        this.defaultValue = null
      }
    },

    setFormDataSearchValue (value) {
      this.formData[`${this.name}${SUFFIX_SEARCH}`] = value
    }
  }
}
</script>

<style scoped lang="scss">
.select-enter,
.select-leave-to {
  transform: translateY(5px);
  opacity: 0;
}

.select-enter-active,
.select-leave-active {
  transition: all 0.2s;
}

.select-enter-to,
.select-leave {
  transform: translateY(0);
  opacity: 1;
}

.select {
  position: relative;
  outline: none;
  text-align: left;

  .validation-error {
    color: $color_error;
    font-size: 12px;
    position: absolute;
  }

  .dropdown_icon {
    stroke: $color_info;
    transition: transform 0.2s;

    &.active {
      transform: rotate(180deg);
    }
  }

  .clear_icon {
    stroke: $color_third-dark;
    stroke-width: 2;
    transition: 0.2s;

    &:hover {
      stroke: $color_error;
    }
  }
}

.select-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 40px;
  max-height: 142px;
  max-width: 100%;
  font-size: $font_main_size;
  color: $color_third-dark;
  transition: all 0.2s;

  border: 1px solid $color_third-dark;
  background: white;
  border-radius: 4px;
  padding: 0 13px;
  outline: none;
  box-sizing: border-box;
  cursor: default;

  &:not(.disabled) {
    cursor: pointer;
  }

  .multiple {
    display: flex;
    flex-shrink: 1;
    flex-wrap: wrap;
    margin-right: 20px;
    overflow: auto;
    max-height: 142px;

    &::-webkit-scrollbar {
      width: 8px;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(0, 66, 105, 0.06);
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 66, 105, 0.24);
      border-radius: 4px;
    }

    .selected__item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: #FFFFFF;
      background: #1170B8;
      border-radius: 3px;
      padding: 0 6px;
      margin: 2px 8px 2px 0;
      height: 36px;
    }
  }

  input::placeholder {
    color: #b8cad5;
    font-size: 16px;
  }

  &.disabled {
    border: 1px solid $color_deactive;
    background: $color_sixth;
  }

  &.error {
    border-color: red;
  }
}

.selectDialog {
  display: flex;
  flex-direction: column;
  position: absolute;
  background: white;
  box-shadow: 0px 10px 20px rgba(38, 40, 135, 0.12), 0px 2px 6px rgba(0, 0, 0, 0.04), 0px 0px 1px rgba(0, 0, 0, 0.04);
  box-sizing: border-box;
  max-height: 264px;
  overflow: auto;
  width: 100%;
  animation: showing 0.2s linear;
  border-radius: 4px;
  z-index: 1000;

  &:not(.before) {
    top: 100%;
  }

  &.before {
    bottom: 100%;
  }

  @keyframes showing {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  .item {
    display: flex;
    align-items: center;
    // justify-content: space-between;
    cursor: pointer;
    border-top: 1px solid rgba(83, 109, 134, 0.1);
    // min-height: 46px;
    padding: 12px;

    &:hover {
      background-color: transparentize($color_second-light, .8);
    }

    &.selected {
      background-color: transparentize($color_second-light, .8);
    }
  }

  div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 20px;
  }

  &::-webkit-scrollbar {
    width: 8px;
    background-color: #ebeeed;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 66, 105, 0.24);
    border-radius: 4px;
  }
}

.multi__clear_icon {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 6px;
  margin-left: 4px;
  transition: 0.2s;

  .clear_icon {
    stroke: #fff;
    stroke-width: 2;
    transition: 0.2s;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.1);

    .clear_icon {
      stroke: $color_warning-second;
    }
  }
}

.icons__control {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-width: 40px;
}

.close {
  position: relative;
  width: 15px;
  height: 15px;
  opacity: 0.3;

  &:hover {
    opacity: .9;
    &::before,
    &::after {
      background-color: $color_warning-second;
    }
  }

  &::before,
  &::after {
    position: absolute;
    left: 50%;
    content: ' ';
    height: 15px;
    width: 2px;
    background-color: $color_third-dark;
    transform: translateX(-50%);
  }

  &::before {
    transform: rotate(45deg);
  }
  &::after {
    transform: rotate(-45deg);
  }
}
</style>
